'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, CheckCircle, ChevronDown } from 'lucide-react';
import { useContactModal } from '../contexts/ContactModalContext';

export default function ContactModal() {
    const { isOpen, closeModal } = useContactModal();

    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', subject: '', message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    // Close on Escape key
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
        if (isOpen) document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, closeModal]);

    // Lock body scroll when open
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, source: 'Service Page Popup' }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Something went wrong.');
            setIsSubmitted(true);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setError('');
    };

    const inputClass = "w-full px-5 py-3.5 rounded-xl bg-gray-50 border border-gray-200 focus:border-blue-500 focus:bg-white focus:outline-none transition-all text-gray-900 placeholder:text-gray-400 text-sm";

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={closeModal}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
                    />

                    {/* Modal Panel */}
                    <motion.div
                        key="modal"
                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 40, scale: 0.96 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-8 pointer-events-none"
                    >
                        <div
                            className="pointer-events-auto bg-white rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-8 pt-8 pb-4">
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">Let's Talk</h2>
                                    <p className="text-gray-500 text-sm mt-0.5">We respond within 24 hours.</p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors shrink-0"
                                >
                                    <X className="w-4 h-4 text-gray-600" />
                                </button>
                            </div>

                            <div className="px-8 pb-8">
                                {isSubmitted ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="text-center py-10"
                                    >
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckCircle className="w-8 h-8 text-green-600" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                                        <p className="text-gray-500 text-sm mb-6">We'll get back to you within 24 hours.</p>
                                        <div className="flex gap-3 justify-center">
                                            <button
                                                onClick={handleReset}
                                                className="text-sm text-blue-600 font-bold hover:underline"
                                            >
                                                Send another
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                onClick={closeModal}
                                                className="text-sm text-gray-600 font-bold hover:underline"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        {/* Name + Email */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-gray-700 uppercase tracking-widest">Full Name *</label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder="John Doe"
                                                    className={inputClass}
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-gray-700 uppercase tracking-widest">Email *</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder="john@example.com"
                                                    className={inputClass}
                                                />
                                            </div>
                                        </div>

                                        {/* Phone */}
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-gray-700 uppercase tracking-widest">Phone Number</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="+91 XXXXX XXXXX"
                                                className={inputClass}
                                            />
                                        </div>

                                        {/* Subject */}
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-gray-700 uppercase tracking-widest">Subject / Service</label>
                                            <div className="relative">
                                                <select
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    required
                                                    className={`w-full px-5 py-3.5 rounded-xl bg-gray-50 border border-gray-200 focus:border-blue-500 focus:bg-white focus:outline-none transition-all appearance-none cursor-pointer pr-10 text-sm ${formData.subject ? 'text-gray-900 font-medium' : 'text-gray-400'}`}
                                                >
                                                    <option value="" disabled hidden>Select a service...</option>
                                                    <option value="Performance Marketing" className="text-gray-900 font-medium">Performance Marketing</option>
                                                    <option value="Visual Communication & Branding" className="text-gray-900 font-medium">Visual Communication & Branding</option>
                                                    <option value="Digital Product Design & Development" className="text-gray-900 font-medium">Digital Product Design & Development</option>
                                                    <option value="Search & Visibility (SEO & SEM)" className="text-gray-900 font-medium">Search & Visibility (SEO & SEM)</option>
                                                    <option value="Social Media Marketing" className="text-gray-900 font-medium">Social Media Marketing</option>
                                                    <option value="General Inquiry / Other" className="text-gray-900 font-medium">General Inquiry / Other</option>
                                                </select>
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                                    <ChevronDown className="w-4 h-4" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Message */}
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-gray-700 uppercase tracking-widest">Message *</label>
                                            <textarea
                                                rows={4}
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                placeholder="Tell us about your project..."
                                                className={`${inputClass} resize-none`}
                                            />
                                        </div>

                                        {error && (
                                            <p className="text-red-500 text-sm text-center font-medium">{error}</p>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-gray-900 hover:bg-black disabled:opacity-60 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-3 transition-all hover:scale-[1.01] active:scale-95 text-sm tracking-widest uppercase mt-2"
                                        >
                                            {isSubmitting ? (
                                                <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                                            ) : (
                                                <>
                                                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                                                        <Send className="w-3 h-3" />
                                                    </span>
                                                    Send Message
                                                </>
                                            )}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
